/** Light metadata for geoprocessing tools. Kept apart from geoprocess.js so UI code can import it
 *  without pulling turf into the main bundle (turf runs in the worker; see lib/worker-pool.js). */

/** Tools that require two inputs. */
export const TWO_INPUT_TOOLS = ['union', 'intersect', 'clip', 'merge'];

// ── Square / hex grid size guard ─────────────────────────────────────────────

/** Grid tools refuse to build more cells than this (a world-wide 10 km grid hung the tab). */
export const MAX_GRID_CELLS = 50000;

const KM_PER_UNIT = { meters: 0.001, kilometers: 1, miles: 1.609344 };

/** Unit abbreviation for result names ("0.5 km", "250 m"). */
export function unitAbbr(unit) {
  return { meters: 'm', kilometers: 'km', miles: 'mi' }[unit] || unit || '';
}

/** Whether [w, s, e, n] is a usable extent. */
export function validExtent(ext) {
  return Array.isArray(ext) && ext.length === 4 && ext.every(Number.isFinite) && ext[2] > ext[0] && ext[3] > ext[1];
}

const KM_PER_DEGREE = 111.195; // turf: earth radius 6371008.8 m

/**
 * Approximate number of cells turf's squareGrid / hexGrid builds over `extent` ([w, s, e, n]
 * in degrees) for `cellSize` in `unit`. Square: turf 7 converts the cell size to plain degrees
 * on both axes, so columns x rows in degrees (rounded up: an upper bound). Hex: extent area
 * on the ground / hexagon area (turf's cellSide is the circumradius).
 */
export function estimateGridCells(extent, cellSize, unit = 'kilometers', type = 'square') {
  if (!validExtent(extent)) return Infinity;
  const c = Number(cellSize) * (KM_PER_UNIT[unit] ?? 1);
  if (!(c > 0)) return Infinity;
  const [w, s, e, n] = extent;
  if (type === 'hex') {
    const midLat = ((s + n) / 2) * (Math.PI / 180);
    const widthKm = (e - w) * KM_PER_DEGREE * Math.max(Math.cos(midLat), 0.01);
    const heightKm = (n - s) * KM_PER_DEGREE;
    const hexArea = ((3 * Math.sqrt(3)) / 2) * c * c;
    return Math.max(1, Math.ceil((widthKm * heightKm) / hexArea));
  }
  const deg = c / KM_PER_DEGREE;
  return Math.max(1, Math.ceil((e - w) / deg)) * Math.max(1, Math.ceil((n - s) / deg));
}

/** Throws a readable error when the grid would exceed `max` cells; returns the estimate. */
export function assertGridSize(extent, cellSize, unit, type, max = MAX_GRID_CELLS) {
  if (!validExtent(extent)) throw new Error('Grid extent is missing: zoom the map to the area or pick an extent layer');
  const cells = estimateGridCells(extent, cellSize, unit, type);
  if (cells > max) {
    throw new Error(`That grid would have about ${Math.round(cells).toLocaleString()} cells (limit ${max.toLocaleString()}). `
      + 'Increase the cell size, zoom in, or pick a smaller extent layer.');
  }
  return cells;
}

/** [w, s, e, n] of a FeatureCollection without turf (null when it has no coordinates). */
export function layerExtent(geojson) {
  let w = Infinity, s = Infinity, e = -Infinity, n = -Infinity;
  const visit = (c) => {
    if (!Array.isArray(c)) return;
    if (typeof c[0] === 'number') {
      if (c[0] < w) w = c[0];
      if (c[0] > e) e = c[0];
      if (c[1] < s) s = c[1];
      if (c[1] > n) n = c[1];
      return;
    }
    c.forEach(visit);
  };
  (geojson?.features || []).forEach((f) => visit(f?.geometry?.coordinates));
  return Number.isFinite(w) && Number.isFinite(s) ? [w, s, e, n] : null;
}

/** Model Builder: new nodes cascade down-right instead of landing at random spots. */
export function cascadePosition(index) {
  const i = Math.max(0, Math.floor(index) || 0);
  const col = Math.floor(i / 6);
  const step = i % 6;
  return { x: 80 + col * 260 + step * 30, y: 60 + step * 90 };
}
